/**
 * Quello che Otto ha letto in un documento: costi e rate, da salvare con un
 * tap.
 *
 * Il tap salva la proposta sul server, cioè esattamente queste righe: non c'è
 * una rilettura del modello in mezzo. Prima di salvare l'host guarda i numeri
 * accanto al documento, perché un numero letto male entrerebbe nei conti di
 * dodici mesi, e un numero plausibile nel posto giusto non ha un'aria
 * sospetta.
 *
 * Le due metà dicono cose diverse, e lo scrivono: i costi entrano nei conti
 * dell'appartamento, le rate sono promemoria e nei conti non entrano.
 */

import { useEffect, useState } from "react";
import { CalendarClock, CheckCircle2, FileText, Receipt } from "lucide-react";
import {
  ETICHETTE_CATEGORIA,
  ETICHETTE_PERIODICITA,
  formatDate,
  formatEur,
  quotaMensile,
  type ChatBlock,
  type StatoPropostaDocumento,
} from "@otto/shared";
import { api } from "../lib/api.ts";
import { messaggioDi } from "../lib/errori.ts";
import { useStore } from "../lib/store.tsx";
import { Card, Pill } from "../ui/primitives.tsx";

export function DocumentoCosti({
  block,
}: {
  block: Extract<ChatBlock, { type: "documento_costi" }>;
}) {
  const store = useStore();
  const [stato, setStato] = useState<StatoPropostaDocumento | null>(null);
  const [inCorso, setInCorso] = useState<"salva" | "scarta" | null>(null);
  const [esito, setEsito] = useState<string | null>(null);
  const { costi, scadenze, fonte } = block.dati;

  // Lo stato vero sta sul server: una scheda riaperta domani deve dire che è
  // già salvata, non offrire di salvarla una seconda volta.
  useEffect(() => {
    let vivo = true;
    void api
      .statoPropostaDocumento(block.propostaId)
      .then((r) => vivo && setStato(r.stato))
      .catch(() => vivo && setStato("proposta"));
    return () => {
      vivo = false;
    };
  }, [block.propostaId]);

  const salva = async () => {
    setInCorso("salva");
    try {
      const r = await api.salvaPropostaDocumento(block.propostaId);
      setStato(r.stato);
      if (!r.giaDecisa) {
        const parti = [
          r.costi > 0
            ? `${r.costi} ${r.costi === 1 ? "costo" : "costi"} nei conti${block.immobile ? ` di ${block.immobile}` : ""}`
            : null,
          r.scadenze > 0
            ? `${r.scadenze} ${r.scadenze === 1 ? "rata" : "rate"} con promemoria`
            : null,
        ].filter(Boolean);
        setEsito(`Salvato: ${parti.join(", ")}.`);
      }
    } catch (e) {
      store.notify(messaggioDi(e));
    } finally {
      setInCorso(null);
    }
  };

  const scarta = async () => {
    setInCorso("scarta");
    try {
      setStato((await api.scartaPropostaDocumento(block.propostaId)).stato);
    } catch (e) {
      store.notify(messaggioDi(e));
    } finally {
      setInCorso(null);
    }
  };

  // Come pagare è uguale per tutte le rate quasi sempre: si dice una volta.
  const note = [...new Set(scadenze.map((s) => s.nota).filter(Boolean))];

  return (
    <Card size="lg" className="animate-rise overflow-hidden">
      <div className="flex items-center gap-3 border-b border-line px-4 py-3">
        <span className="grid size-9 shrink-0 place-items-center rounded-field bg-surface-muted text-ink-soft">
          <FileText size={17} />
        </span>
        <div className="min-w-0">
          <p className="truncate text-[14.5px] font-bold text-ink">
            Letto in «{fonte}»
          </p>
          <p className="text-[12.5px] text-ink-faint">
            {block.immobile ?? "Nessun appartamento in particolare"}
          </p>
        </div>
      </div>

      <div className="flex flex-col gap-4 px-4 py-4">
        {costi.length > 0 ? (
          <section>
            <p className="mb-2 flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-[0.06em] text-ink-faint">
              <Receipt size={13} /> Costi · entrano nei conti
            </p>
            <div className="flex flex-col gap-1.5">
              {costi.map((c, i) => (
                <div
                  key={i}
                  className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-0.5 rounded-field bg-surface-muted px-3 py-2"
                >
                  <div className="min-w-0">
                    <p className="text-[13.5px] font-semibold text-ink">
                      {c.descrizione}
                    </p>
                    <p className="text-[12px] text-ink-faint">
                      {ETICHETTE_CATEGORIA[c.categoria]} · dal{" "}
                      {formatDate(c.dal)}
                      {c.al ? ` al ${formatDate(c.al)}` : ""}
                    </p>
                  </div>
                  <p className="text-right tabular-nums">
                    <span className="text-[14px] font-bold text-ink">
                      {formatEur(c.importoCents)}
                    </span>
                    <span className="text-[12px] text-ink-soft">
                      {" "}
                      {ETICHETTE_PERIODICITA[c.periodicita].toLowerCase()}
                    </span>
                    <span className="block text-[12px] text-ink-faint">
                      ≈ {formatEur(quotaMensile(c.importoCents, c.periodicita))}{" "}
                      al mese
                    </span>
                  </p>
                </div>
              ))}
            </div>
          </section>
        ) : null}

        {scadenze.length > 0 ? (
          <section>
            <p className="mb-2 flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-[0.06em] text-ink-faint">
              <CalendarClock size={13} /> Rate · promemoria, non entrano nei
              conti
            </p>
            <div className="flex flex-col gap-1">
              {scadenze.map((s, i) => (
                <div
                  key={i}
                  className="flex items-baseline justify-between gap-4 border-b border-line py-1.5 text-[13.5px] last:border-0"
                >
                  <span className="min-w-0 text-ink">
                    <b className="tabular-nums">{formatDate(s.data)}</b>
                    <span className="text-ink-soft"> · {s.descrizione}</span>
                  </span>
                  <b className="shrink-0 tabular-nums text-ink">
                    {formatEur(s.importoCents)}
                  </b>
                </div>
              ))}
            </div>
            <div className="flex items-baseline justify-between gap-4 pt-2 text-[13.5px]">
              <span className="font-semibold text-ink-soft">
                Totale delle rate
              </span>
              <b className="tabular-nums text-ink">
                {formatEur(scadenze.reduce((s, r) => s + r.importoCents, 0))}
              </b>
            </div>
            {note.length > 0 ? (
              <p className="mt-2 text-[12.5px] leading-relaxed text-ink-soft">
                Come pagare: {note.join(" · ")}
              </p>
            ) : null}
          </section>
        ) : null}
      </div>

      <div className="flex flex-wrap items-center gap-2 border-t border-line bg-surface-muted/50 px-4 py-3">
        {stato === "salvata" ? (
          <p className="flex items-center gap-1.5 text-[13.5px] font-semibold text-ok">
            <CheckCircle2 size={16} /> {esito ?? "Salvato."}
          </p>
        ) : stato === "scartata" ? (
          <p className="text-[13.5px] text-ink-faint">
            Scartato: non è stato salvato niente.
          </p>
        ) : (
          <>
            <Pill
              variant="primary"
              size="md"
              onClick={() => void salva()}
              loading={inCorso === "salva"}
              disabled={stato === null || inCorso !== null}
            >
              Salva
            </Pill>
            <Pill
              variant="secondary"
              size="md"
              onClick={() => void scarta()}
              loading={inCorso === "scarta"}
              disabled={stato === null || inCorso !== null}
            >
              Scarta
            </Pill>
            <p className="ml-auto text-[12.5px] text-ink-faint">
              Controlla i numeri con il documento prima di salvare.
            </p>
          </>
        )}
      </div>
    </Card>
  );
}
